import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { useMinuteClock } from "@/hooks/useMinuteClock";
import type { DriverRow } from "@/types/driver";
import { CoordinatesLink } from "@/ui/CoordinatesLink";
import { zinc } from "@/ui/tokens";
import { LOCATION_TOOLTIP_ENTER_DELAY, locationProvenanceLabel, locationStaleness } from "@/utils/locationStaleness";
import { locationText } from "@/utils/locationText";

/** The roster fields that each show one part of the driver's location. */
export type LocationColumnField = "location" | "stateProvince" | "country";

/**
 * One part of a driver's location, with its source and age on hover, greyed once the location is stale.
 *
 * The Location column shows `locationText`, its coordinates fallback linked to Google Maps; State/Province and Country
 * show the structured address part. Renders nothing when the row has no value for `field`.
 */
export function LocationCell({
  driver,
  field,
  testId,
}: {
  driver: DriverRow;
  field: LocationColumnField;
  testId?: string;
}) {
  const now = useMinuteClock();
  const shown = cellText(driver, field);
  if (!shown) return null;

  const staleness = locationStaleness(driver.locationUpdatedAt, now);
  return (
    <Tooltip
      title={locationProvenanceLabel(staleness, driver.locationSource)}
      enterDelay={LOCATION_TOOLTIP_ENTER_DELAY.toMillis()}
      enterNextDelay={LOCATION_TOOLTIP_ENTER_DELAY.toMillis()}
    >
      <Box component="span" data-sb={testId} sx={staleness.stale ? { color: zinc[600] } : undefined}>
        {shown.coordinates ? (
          <CoordinatesLink
            latitude={driver.latitude}
            longitude={driver.longitude}
            testId={TestIds.driverView.locationCoordinates}
          >
            {shown.text}
          </CoordinatesLink>
        ) : (
          shown.text
        )}
      </Box>
    </Tooltip>
  );
}

function cellText(driver: DriverRow, field: LocationColumnField): { text: string; coordinates: boolean } | null {
  if (field === "location") return locationText(driver);
  const value = driver[field];
  return value ? { text: value, coordinates: false } : null;
}
